/**
 * MediaEmbed Component
 * Renders embeds for YouTube, Instagram, and TikTok content
 * Supports dynamic autoplay control based on section visibility
 * Includes improved skeleton loading and lazy loading support
 */

import React, { useState, useRef, useEffect } from 'react';

// Skeleton loader with animated shimmer effect
const MediaSkeleton = () => (
  <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse">
    <div className="h-full flex flex-col items-center justify-center space-y-4">
      {/* Play button skeleton */}
      <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
        <div className="w-0 h-0 border-l-[12px] border-l-gray-400 border-y-[8px] border-y-transparent ml-1"></div>
      </div>
      {/* Text skeletons */}
      <div className="space-y-2 w-3/4">
        <div className="h-3 bg-gray-300 rounded w-full"></div>
        <div className="h-3 bg-gray-300 rounded w-2/3 mx-auto"></div>
      </div>
    </div>
    {/* Shimmer overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skeleton-shimmer"></div>
  </div>
);

const MediaEmbed = ({
  item,
  className = '',
  aspectRatio = '16/9',
  autoplay = false,
  fillParent = false,
  lazy = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shouldRender, setShouldRender] = useState(!lazy);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (!lazy || shouldRender) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, shouldRender]);

  // Handle autoplay changes for video elements
  useEffect(() => {
    if (videoRef.current) {
      if (autoplay) {
        // Mute and play when autoplay is enabled
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {
          // Autoplay may be blocked by browser, that's ok
        });
      } else {
        // Pause when autoplay is disabled (scrolled away)
        videoRef.current.pause();
      }
    }
  }, [autoplay]);

  // Handle autoplay changes for YouTube iframes via postMessage
  useEffect(() => {
    if (iframeRef.current && item.platform === 'youtube') {
      try {
        const command = autoplay ? 'playVideo' : 'pauseVideo';
        iframeRef.current.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: command }),
          '*'
        );
      } catch {
        // Cross-origin restrictions may prevent this
      }
    }
  }, [autoplay, item.platform]);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const renderYouTube = () => {
    // Build URL with parameters to minimize YouTube branding
    const params = new URLSearchParams({
      modestbranding: '1',  // Minimize YouTube branding
      rel: '0',             // Show related videos from same channel only
      iv_load_policy: '3',  // Hide video annotations
      enablejsapi: '1',     // Enable JS API for play/pause control
      ...(autoplay && {
        autoplay: '1',
        mute: '1',
        loop: '1',
        playlist: item.id,
      }),
    });

    const embedUrl = `${item.embedUrl}?${params.toString()}`;

    return (
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={item.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        onLoad={handleLoad}
        onError={handleError}
        loading={lazy ? 'lazy' : 'eager'}
      />
    );
  };

  const renderInstagram = () => {
    if (item.type === 'video' || item.isReel) {
      return (
        <video
          ref={videoRef}
          src={item.mediaUrl}
          poster={item.thumbnailUrl}
          controls
          loop
          muted={autoplay}
          autoPlay={autoplay}
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          onLoadedData={handleLoad}
          onError={handleError}
          preload={lazy ? 'none' : 'metadata'}
        />
      );
    }

    return (
      <img
        src={item.mediaUrl}
        alt={item.caption || 'Instagram post'}
        className="absolute inset-0 w-full h-full object-cover"
        onLoad={handleLoad}
        onError={handleError}
        loading={lazy ? 'lazy' : 'eager'}
      />
    );
  };

  const renderTikTok = () => {
    return (
      <iframe
        src={item.embedUrl}
        title="TikTok video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        onLoad={handleLoad}
        onError={handleError}
        loading={lazy ? 'lazy' : 'eager'}
      />
    );
  };

  const renderFallback = () => {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
        <div className="text-center p-4">
          <p className="text-gray-500 mb-2">Content unavailable</p>
          {item.url && (
            <a
              href={item.url || item.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline text-sm"
            >
              View on {item.platform}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`media-container ${fillParent ? 'h-full' : ''} ${className}`}
      style={fillParent ? {} : { aspectRatio }}
    >
      {/* Loading skeleton */}
      {(loading || !shouldRender) && <MediaSkeleton />}

      {/* Error state */}
      {error && renderFallback()}

      {/* Media content - only render when shouldRender is true */}
      {!error && shouldRender && (
        <>
          {item.platform === 'youtube' && renderYouTube()}
          {item.platform === 'instagram' && renderInstagram()}
          {item.platform === 'tiktok' && renderTikTok()}
        </>
      )}

      {/* Overlay info (optional) */}
      {item.caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white opacity-0 hover:opacity-100 transition-opacity">
          <p className="text-sm line-clamp-2">{item.caption || item.title}</p>
        </div>
      )}
    </div>
  );
};

export default MediaEmbed;
